/**
 * Inventory Report Page - print-friendly view of all boxes grouped by location.
 */

import { useBoxStore } from '../store/BoxContext';

export function InventoryReportPage() {
  const { state } = useBoxStore();

  // Group boxes by location
  const boxesByLocation = state.boxes.reduce(
    (acc, box) => {
      const location = box.location || 'No Location';
      if (!acc[location]) {
        acc[location] = [];
      }
      acc[location].push(box);
      return acc;
    },
    {} as Record<string, typeof state.boxes>,
  );

  const locations = Object.keys(boxesByLocation).sort();

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media print {
          body { margin: 0; padding: 20px; }
          .no-print { display: none !important; }
          .page-break { page-break-before: always; }
          h1 { font-size: 24pt; margin-bottom: 12pt; }
          h2 { font-size: 18pt; margin-top: 16pt; margin-bottom: 8pt; }
          h3 { font-size: 14pt; margin-top: 8pt; margin-bottom: 4pt; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 16pt; }
          th, td { border: 1px solid #000; padding: 8pt; text-align: left; }
          th { background-color: #f0f0f0; font-weight: bold; }
        }
        @media screen {
          .inventory-report { max-width: 8.5in; margin: 0 auto; padding: 20px; }
        }
      `,
        }}
      />

      <div className="inventory-report">
        <div className="no-print mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-text-primary">Inventory Report</h1>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 flex items-center gap-2"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            Print Report
          </button>
        </div>

        <h1>Box Inventory Report</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
        </p>

        <p style={{ marginBottom: '20px' }}>
          <strong>Total Boxes:</strong> {state.boxes.length}
        </p>

        {locations.map((location, idx) => (
          <div key={location} className={idx > 0 ? 'page-break' : ''}>
            <h2>{location}</h2>

            {boxesByLocation[location].map((box) => (
              <div key={box.id} style={{ marginBottom: '24px' }}>
                <h3>
                  {box.name}
                  <span
                    style={{
                      fontSize: '12pt',
                      fontWeight: 'normal',
                      marginLeft: '8px',
                      color: '#666',
                    }}
                  >
                    ({box.markerType === 'aruco' ? `ArUco #${box.markerId}` : `QR: ${box.markerId}`}
                    )
                  </span>
                </h3>

                {box.notes && (
                  <p style={{ marginBottom: '8px', fontStyle: 'italic', color: '#666' }}>
                    {box.notes}
                  </p>
                )}

                <p style={{ color: '#999', fontStyle: 'italic' }}>Items not loaded in list view</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}
